import { Box, Button, InputLabel, LinearProgress, Paper, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useState, useRef } from 'react'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const Totalizadores = () => {
  const { t } = useTranslation(['common', 'messages'])
  const [selectedFile, setSelectedFile] = useState(null)
  const [maxAccounts, setMaxAccounts] = useState(1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [progress, setProgress] = useState(0)
  const fileInputRef = useRef(null)

  const steps = [
    { label: t('common:sending'), duration: 8000 },
    { label: t('common:updatingTotalizers'), duration: 12000 },
    { label: t('common:updatingLists'), duration: 10000 },
    { label: t('common:completed'), duration: 2000 },
  ]

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFile(file)
      setCurrentStep(0)
      setProgress(0)
      setIsProcessing(false)
      setIsCompleted(false)
    }
  }

  const handleMaxAccountsChange = (event) => {
    const value = parseInt(event.target.value, 10)
    if (isNaN(value)) {
      setMaxAccounts(1)
    } else if (value < 1) {
      setMaxAccounts(1)
    } else if (value > 100) {
      setMaxAccounts(100)
    } else {
      setMaxAccounts(value)
    }
  }

  const handleCancel = () => {
    setSelectedFile(null)
    setMaxAccounts(1)
    setIsProcessing(false)
    setIsCompleted(false)
    setCurrentStep(0)
    setProgress(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsProcessing(true)
    setIsCompleted(false)
    setCurrentStep(0)
    setProgress(0)

    // Simular proceso de carga con steps
    for (let i = 0; i < steps.length; i++) {
      setCurrentStep(i)
      const stepDuration = steps[i].duration
      const intervalTime = 50
      const increments = stepDuration / intervalTime
      const progressIncrement = 100 / increments

      for (let j = 0; j <= increments; j++) {
        await new Promise((resolve) => setTimeout(resolve, intervalTime))
        setProgress((prev) => Math.min(prev + progressIncrement / steps.length, ((i + 1) / steps.length) * 100))
      }
    }

    // Marcar como completado
    setIsCompleted(true)
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <Box className="filter">
      <Box className="component-title">
        <Typography variant="h2">{t('common:totalizadores')}</Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '500px',
          padding: '3rem',
          gap: '2rem',
        }}
      >
        {!isProcessing && !isCompleted ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1.5rem',
                width: '100%',
                maxWidth: '600px',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                accept="*/*"
              />
              <label htmlFor="file-upload" style={{ width: '100%' }}>
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: '8px',
                    padding: '3rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: '4rem', color: '#1976d2', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('common:selectFile')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('common:clickToSelectFile')}
                  </Typography>
                </Box>
              </label>

              {selectedFile && (
                <Paper
                  elevation={1}
                  sx={{
                    width: '100%',
                    padding: '1.5rem',
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    {t('common:selectedFile')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('common:size')}: {formatFileSize(selectedFile.size)}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ width: '100%' }}>
                <InputLabel htmlFor="max-accounts">{t('common:maxAccountsAllowed')}</InputLabel>
                <TextField
                  id="max-accounts"
                  type="number"
                  value={maxAccounts}
                  onChange={handleMaxAccountsChange}
                  size="small"
                  fullWidth
                  inputProps={{
                    min: 1,
                    max: 100,
                  }}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '1rem', mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={!selectedFile}
                size="large"
              >
                {t('common:cancel')}
              </Button>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={handleUpload}
                disabled={!selectedFile}
                size="large"
              >
                {t('common:load')}
              </Button>
            </Box>
          </>
        ) : isProcessing && !isCompleted ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: '600px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>
                {steps[currentStep].label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('common:processingFile')} {selectedFile?.name}
              </Typography>
            </Box>

            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: '10px',
                  borderRadius: '5px',
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                {Math.round(progress)}%
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {steps.map((step, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    opacity: index <= currentStep ? 1 : 0.4,
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: index < currentStep ? '#4caf50' : index === currentStep ? '#1976d2' : '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    {index < currentStep ? '✓' : index + 1}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: index === currentStep ? 'bold' : 'normal',
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              maxWidth: '600px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2rem',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: '6rem', color: '#4caf50' }} />
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold' }}>
              {t('common:completed')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
              {t('common:file')}: {selectedFile?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              {t('common:processingFile')} {selectedFile?.name}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default Totalizadores
